import React, { useState, useEffect } from 'react';
import { 
  Star, Search, Filter, MessageSquare, Check, CornerDownRight,
  TrendingUp, Award, Users, Calendar, ThumbsUp, Reply, Send,
  Image as ImageIcon, X, ChevronLeft, ChevronRight, Download,
  Smile, Heart, AlertCircle, Clock, User as UserIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Reviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('All');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        let token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          token = userInfo.token;
        }

        if (!token) {
          toast.error("Please login");
          return navigate('/account');
        }

        const res = await axios.get('/api/reviews/seller', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const fetchedReviews = res.data.data.map(rev => ({
          id: rev._id,
          productId: rev.product?._id,
          productName: rev.product?.name || 'Unknown Product',
          productImage: rev.product?.image || '/assets/images/thumbs/plant-placeholder.png',
          rating: rev.rating,
          message: rev.message,
          customer: rev.user?.username || 'Anonymous',
          customerAvatar: rev.user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(rev.user?.username || 'User')}&background=10b981&color=fff`,
          date: new Date(rev.createdAt),
          reply: rev.reply,
          helpful: rev.helpful || 0
        }));
        
        setReviews(fetchedReviews);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        toast.error('Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [navigate]);

  const totalReviews = reviews.length;
  const avgRating = totalReviews > 0 ? (reviews.reduce((acc, rev) => acc + rev.rating, 0) / totalReviews).toFixed(1) : 0;
  const fiveStarCount = reviews.filter(r => r.rating === 5).length;
  const fourStarCount = reviews.filter(r => r.rating === 4).length;
  const repliedCount = reviews.filter(r => r.reply).length;

  const filteredReviews = reviews.filter(rev => {
    const matchSearch = rev.productName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                       rev.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = filterRating === 'All' || rev.rating.toString() === filterRating;
    return matchSearch && matchFilter;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);

  const handleReplySubmit = async (reviewId) => {
    if (!replyText.trim()) {
      toast.error("Reply cannot be empty.");
      return;
    }

    try {
      let token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        token = userInfo.token;
      }

      const res = await axios.put(`/api/reviews/${reviewId}/reply`, { reply: replyText.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedReviews = reviews.map(rev => 
        rev.id === reviewId ? { ...rev, reply: res.data.data.reply } : rev
      );

      setReviews(updatedReviews);
      setReplyingTo(null);
      setReplyText('');
      toast.success("Reply posted successfully!");
    } catch (error) {
      console.error('Error posting reply:', error);
      toast.error(error.response?.data?.message || 'Failed to post reply');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star 
        key={index} 
        size={14} 
        fill={index < rating ? "#f59e0b" : "transparent"} 
        stroke={index < rating ? "#f59e0b" : "#d1d5db"} 
        className="star-icon"
      />
    ));
  };

  const StatCard = ({ title, value, icon: Icon, subtitle, color, bgColor }) => (
    <div className="stat-card-review">
      <div className="stat-icon-review" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-content-review">
        <h3 className="stat-value-review">{value}</h3>
        <p className="stat-title-review">{title}</p>
        {subtitle && <span className="stat-subtitle-review">{subtitle}</span>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container-review">
        <div className="spinner-review"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="reviews-page">
      {/* Header */}
      <div className="reviews-header">
        <div className="header-left">
          <h1 className="page-title-review">Customer Reviews</h1>
          <p className="page-subtitle-review">Monitor feedback and respond to your customers</p>
        </div>
        <button className="btn-export-review" onClick={() => toast.info('Export feature coming soon')}>
          <Download size={16} />
          Export Reviews
        </button>
      </div>

      {/* Stats Grid */}
      <div className="stats-review-grid">
        <StatCard 
          title="Average Rating" 
          value={avgRating} 
          icon={Star} 
          color="#f59e0b" 
          bgColor="#fed7aa"
          subtitle="out of 5"
        />
        <StatCard 
          title="Total Reviews" 
          value={totalReviews} 
          icon={MessageSquare} 
          color="#3b82f6" 
          bgColor="#dbeafe"
        />
        <StatCard 
          title="5-Star Reviews" 
          value={fiveStarCount} 
          icon={Award} 
          color="#10b981" 
          bgColor="#d1fae5"
          subtitle={`${Math.round((fiveStarCount/totalReviews)*100) || 0}% of total`}
        />
        <StatCard 
          title="Replied" 
          value={repliedCount} 
          icon={Check} 
          color="#8b5cf6" 
          bgColor="#e9d5ff"
          subtitle={`${Math.round((repliedCount/totalReviews)*100) || 0}% response rate`}
        />
      </div>

      {/* Rating Distribution */}
      <div className="rating-distribution-card">
        <div className="distribution-header">
          <h5 className="section-title-review">Rating Distribution</h5>
          <p className="section-subtitle-review">Breakdown of customer ratings</p>
        </div>
        <div className="distribution-bars">
          {[5,4,3,2,1].map(star => {
            const count = reviews.filter(r => r.rating === star).length;
            const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            return (
              <div key={star} className="distribution-bar-item">
                <div className="star-label">
                  <span>{star} Star</span>
                  <Star size={12} fill="#f59e0b" color="#f59e0b" />
                </div>
                <div className="progress-bar-wrapper">
                  <div className="progress-bar-fill" style={{ width: `${percentage}%`, backgroundColor: '#10b981' }}></div>
                </div>
                <div className="percentage-label">{Math.round(percentage)}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-review-card">
        <div className="search-wrapper-review">
          <Search size={18} className="search-icon-review" />
          <input 
            type="text" 
            className="search-input-review" 
            placeholder="Search by product or customer..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="rating-filters">
          <button 
            className={`rating-filter-btn ${filterRating === 'All' ? 'active' : ''}`}
            onClick={() => setFilterRating('All')}
          >
            All
          </button>
          {[5,4,3,2,1].map(star => (
            <button 
              key={star}
              className={`rating-filter-btn ${filterRating === star.toString() ? 'active' : ''}`}
              onClick={() => setFilterRating(star.toString())}
            >
              {star} <Star size={10} fill="#f59e0b" color="#f59e0b" />
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {filteredReviews.length === 0 ? (
        <div className="empty-state-review">
          <div className="empty-icon-wrapper">
            <MessageSquare size={64} />
          </div>
          <h3>No reviews found</h3>
          <p>We couldn't find any reviews matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="reviews-list">
            {currentReviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-avatar">
                      <img src={review.customerAvatar} alt={review.customer} />
                    </div>
                    <div>
                      <h6 className="reviewer-name">{review.customer}</h6>
                      <div className="review-meta">
                        <span className="meta-date">
                          <Calendar size={12} />
                          {review.date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="meta-product">
                          <ImageIcon size={12} />
                          {review.productName}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="review-rating">
                    {renderStars(review.rating)}
                    <span className="rating-value">{review.rating}.0</span>
                  </div>
                </div>
                
                <div className="review-body">
                  <p className="review-message">"{review.message}"</p>
                  
                  <div className="review-product-preview">
                    <img src={review.productImage} alt={review.productName} />
                    <div className="product-preview-info">
                      <span className="product-preview-name">{review.productName}</span>
                      <span className="product-preview-id">ID: {review.productId}</span>
                    </div>
                  </div>

                  {review.reply ? (
                    <div className="reply-section replied">
                      <div className="reply-header">
                        <div className="reply-avatar">
                          <Check size={14} color="#10b981" />
                        </div>
                        <div>
                          <span className="reply-label">Your Reply</span>
                          <span className="reply-date">
                            <Clock size={10} />
                            Responded
                          </span>
                        </div>
                      </div>
                      <p className="reply-message">{review.reply}</p>
                    </div>
                  ) : (
                    replyingTo === review.id ? (
                      <div className="reply-section replying">
                        <div className="reply-input-wrapper">
                          <textarea 
                            className="reply-input"
                            rows="3" 
                            placeholder="Write your reply to the customer..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            autoFocus
                          />
                          <div className="reply-actions">
                            <button 
                              className="btn-cancel-reply" 
                              onClick={() => { setReplyingTo(null); setReplyText(''); }}
                            >
                              Cancel
                            </button>
                            <button 
                              className="btn-submit-reply" 
                              onClick={() => handleReplySubmit(review.id)}
                            >
                              <Send size={14} />
                              Post Reply
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button 
                        className="btn-reply-review"
                        onClick={() => { setReplyingTo(review.id); setReplyText(''); }}
                      >
                        <MessageSquare size={14} />
                        Reply to Review
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-review">
              <button 
                className="page-btn-review" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <div className="page-numbers-review">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`page-number-review ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button 
                className="page-btn-review" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      <style>{`.reviews-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-review {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.page-subtitle-review {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.btn-export-review {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-export-review:hover {
  border-color: #10b981;
  color: #10b981;
}

.stats-review-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card-review {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.stat-card-review:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.stat-icon-review {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}

.stat-value-review {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.stat-title-review {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}

.stat-subtitle-review {
  font-size: 11px;
  color: #94a3b8;
}

.rating-distribution-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.distribution-header {
  margin-bottom: 20px;
}

.section-title-review {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.section-subtitle-review {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

.distribution-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.distribution-bar-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.star-label {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 70px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.progress-bar-wrapper {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 20px;
  transition: width 0.5s ease;
}

.percentage-label {
  width: 45px;
  font-size: 12px;
  color: #64748b;
  text-align: right;
}

.filters-review-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.search-wrapper-review {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.search-icon-review {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input-review {
  width: 100%;
  padding: 10px 16px 10px 42px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input-review:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.rating-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.rating-filter-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.rating-filter-btn.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.review-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.review-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.reviewer-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reviewer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #f1f5f9;
}

.reviewer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reviewer-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px;
}

.review-meta {
  display: flex;
  gap: 16px;
  align-items: center;
}

.meta-date,
.meta-product {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.review-rating {
  display: flex;
  align-items: center;
  gap: 6px;
}

.star-icon {
  margin-right: 2px;
}

.rating-value {
  font-size: 14px;
  font-weight: 600;
  color: #f59e0b;
  margin-left: 6px;
}

.review-body {
  flex: 1;
}

.review-message {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 16px;
  font-style: italic;
}

.review-product-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 16px;
}

.review-product-preview img {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
}

.product-preview-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  display: block;
  margin-bottom: 2px;
}

.product-preview-id {
  font-size: 10px;
  color: #94a3b8;
  font-family: monospace;
}

.reply-section {
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
}

.reply-section.replied {
  background: #f0fdf4;
  border: 1px solid #d1fae5;
}

.reply-section.replying {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.reply-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.reply-avatar {
  width: 28px;
  height: 28px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reply-label {
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
}

.reply-date {
  font-size: 10px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 10px;
}

.reply-message {
  font-size: 13px;
  color: #475569;
  margin: 0;
}

.reply-input-wrapper {
  width: 100%;
}

.reply-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 13px;
  resize: vertical;
  margin-bottom: 12px;
}

.reply-input:focus {
  outline: none;
  border-color: #10b981;
}

.reply-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-reply-review {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #f1f5f9;
  border: none;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
}

.btn-reply-review:hover {
  background: #e2e8f0;
  color: #10b981;
}

.btn-cancel-reply {
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-submit-reply {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;
  background: #10b981;
  border: none;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-submit-reply:hover {
  background: #059669;
  transform: translateY(-1px);
}

.pagination-review {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-btn-review {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn-review:hover:not(:disabled) {
  border-color: #10b981;
  color: #10b981;
}

.page-btn-review:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers-review {
  display: flex;
  gap: 6px;
}

.page-number-review {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-number-review.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.page-number-review:hover:not(.active) {
  border-color: #10b981;
  color: #10b981;
}

.empty-state-review {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 24px;
}

.loading-container-review {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
}

.spinner-review {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@media (max-width: 768px) {
  .reviews-page {
    padding: 16px;
  }

  .stats-review-grid {
    grid-template-columns: 1fr;
  }

  .filters-review-card {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrapper-review {
    max-width: none;
  }

  .review-header {
    flex-direction: column;
  }

  .distribution-bar-item {
    flex-wrap: wrap;
  }

  .star-label {
    width: 100%;
    margin-bottom: 6px;
  }

  .percentage-label {
    width: auto;
  }
}`}</style>
    </div>
  );
};

export default Reviews;